import { isLiteTemplate } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Checkbox, Group, Modal, ScrollArea, Stack, Text, TextInput } from "@mantine/core"
import { IconAlertTriangle, IconArrowsJoin, IconSearch } from "@tabler/icons-react"
import { useMemo } from "react"
import { formatDateTime } from "../../../lib/format-date"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import type { IMergeableMeeting } from "../interfaces"
import type { IViewProps } from "./interfaces"

/**
 * Dumb view for MergeMeetingsDialog. Renders the modal — search, list of
 * mergeable meetings with template-lock disabling, the discard-edits confirm
 * alert, and the primary merge CTA. Owns the destructive-confirm visual flow
 * but emits events; the smart wrapper at ../index.tsx owns mutation state
 * and runs preflight + merge against the company API.
 */
export const MergeMeetingsDialogView = ({
  opened,
  onClose,
  meetings,
  query,
  onQueryChange,
  selectedIds,
  templateLock,
  onToggle,
  pendingEditSubjects,
  onConfirmDiscardEdits,
  onCancelEditConfirm,
  onMerge,
  submitting,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    return meetings
      .filter((m) => (q ? m.subject.toLowerCase().includes(q) : true))
      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
  }, [meetings, query])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const editedSubjects = pendingEditSubjects ? pendingEditSubjects.join(", ") : ""
  const awaitingConfirm = pendingEditSubjects !== null && pendingEditSubjects.length > 0

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Trans>Merge meetings</Trans>}
      size="lg"
      centered
      withCloseButton={!submitting}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          <Trans>
            Pick 2 or more meetings to combine. The audio from each is concatenated in start-time order and the merged
            recording is re-processed from scratch. Originals stay in the database but are hidden from the default list.
          </Trans>
        </Text>

        {awaitingConfirm && (
          <Alert
            color="warning"
            icon={<IconAlertTriangle size={16} />}
            title={<Trans>Manual edits will be lost</Trans>}
          >
            <Text size="sm">
              <Trans>
                The following meetings have manual field edits that won't carry over: {editedSubjects}. Continue and the
                merge will reprocess from raw audio, discarding those edits.
              </Trans>
            </Text>
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" onClick={onCancelEditConfirm}>
                <Trans>Cancel</Trans>
              </Button>
              <Button color="warning" loading={submitting} onClick={onConfirmDiscardEdits}>
                <Trans>Discard edits and merge</Trans>
              </Button>
            </Group>
          </Alert>
        )}

        <TextInput
          placeholder={i18n._(msg`Search meetings…`)}
          leftSection={<IconSearch size={14} />}
          value={query}
          onChange={(e) => onQueryChange(e.currentTarget.value)}
        />

        <ScrollArea h={320}>
          <Stack gap={4}>
            {filtered.map((m: IMergeableMeeting) => {
              const isSelected = selectedSet.has(m.id)
              const disabledByTemplate =
                templateLock !== null && (m.template_id ?? null) !== templateLock && !isSelected
              return (
                <Checkbox
                  key={m.id}
                  checked={isSelected}
                  onChange={() => onToggle(m)}
                  disabled={disabledByTemplate}
                  label={
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {displayMeetingTitle(m.subject, null, i18n, {
                          lite: isLiteTemplate(m.template_id),
                          startedAt: m.start_time,
                        })}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(i18n, m.start_time, { year: false })} · {m.status}
                        {disabledByTemplate ? ` · ${i18n._(msg`(different template)`)}` : ""}
                      </Text>
                    </Stack>
                  }
                />
              )
            })}
            {filtered.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                <Trans>No meetings match.</Trans>
              </Text>
            )}
          </Stack>
        </ScrollArea>

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            <Trans>{selectedIds.length} selected</Trans>
          </Text>
          <Group>
            <Button variant="subtle" onClick={onClose} disabled={submitting}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              leftSection={<IconArrowsJoin size={14} />}
              disabled={selectedIds.length < 2 || submitting || awaitingConfirm}
              loading={submitting && !awaitingConfirm}
              onClick={onMerge}
            >
              <Trans>Merge {selectedIds.length} meetings</Trans>
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  )
}
